import { QUERY_MAX_LEN, sanitizeQuery } from '../lib/sanitizeQuery.js';
import { getConsentHistory, setConsentHistory } from '../lib/visitorSession.js';

/**
 * Campo único de intenção — hero minimalista + aviso de histórico.
 */
export class SearchHero {
  /**
   * @param {HTMLElement} root
   * @param {{
   *   onSubmit: (text: string) => void | Promise<void>,
   *   onReplay?: (queryId: string) => void | Promise<void>,
   *   onSuggest?: (query: string) => void | Promise<void>,
   *   onFocusChange?: (focused: boolean) => void,
   *   onQueryChange?: (query: string) => void,
   * }} opts
   */
  constructor(root, { onSubmit, onReplay, onSuggest, onFocusChange, onQueryChange }) {
    this.root = root;
    this.onSubmit = onSubmit;
    this.onReplay = onReplay;
    this.onSuggest = onSuggest;
    this.onFocusChange = onFocusChange;
    this.onQueryChange = onQueryChange;
    this._busy = false;
    this._history = [];
    this._suggestions = [];
    setConsentHistory(true);
    this._render();
  }

  setBusy(busy) {
    this._busy = busy;
    this.form?.classList.toggle('is-busy', busy);
    if (this.input) this.input.disabled = busy;
    if (this.submitBtn) this.submitBtn.disabled = busy;
  }

  focus() {
    this.input?.focus();
  }

  /**
   * @param {{ history?: Array<{id: string, query: string}>, suggestions?: Array<{label: string, query: string, reason?: string}> }} bundle
   */
  setMemory(bundle = {}) {
    this._history = Array.isArray(bundle.history) ? bundle.history : [];
    this._suggestions = Array.isArray(bundle.suggestions)
      ? bundle.suggestions
      : [];
    this._renderMemory();
  }

  _render() {
    getConsentHistory();
    this.root.innerHTML = `
      <div class="intent-hero__inner">
        <p class="intent-hero__brand">Walter Andrade Neto</p>
        <p class="intent-hero__role">Data Engineering Tech Lead · Analytics Engineer</p>
        <h1 class="intent-hero__question" id="intent-question">
          O que você procura em meu portfólio?
        </h1>
        <p class="intent-hero__hint">
          Descreva o projeto ou solução que você precisa
          <span class="intent-hero__hint-cap">(máx. ${QUERY_MAX_LEN} caracteres)</span>
        </p>
        <form class="intent-form" id="intent-form" novalidate>
          <label class="intent-form__label" for="intent-input">Intenção de busca</label>
          <div class="intent-form__field">
            <input
              id="intent-input"
              class="intent-form__input"
              type="text"
              name="query"
              maxlength="${QUERY_MAX_LEN}"
              autocomplete="off"
              spellcheck="true"
              enterkeyhint="search"
              aria-describedby="intent-question intent-counter intent-error"
              placeholder="Ex.: reduzir custo de lakehouse no GCP"
            />
            <button class="intent-form__submit" type="submit" aria-label="Projetar tabuleiro">
              <span class="intent-form__submit-label">Projetar</span>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
                <path fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13 6l6 6-6 6"/>
              </svg>
            </button>
          </div>
          <div class="intent-form__meta">
            <span class="intent-form__counter" id="intent-counter" aria-live="polite">0 / ${QUERY_MAX_LEN}</span>
            <span class="intent-form__error" id="intent-error" role="alert" hidden></span>
          </div>
        </form>
        <div class="intent-memory" id="intent-memory" hidden></div>
        <nav class="intent-hero__links" aria-label="Atalhos">
          <a href="./perfil.html" target="_blank" rel="noopener">Perfil completo</a>
          <a href="https://www.linkedin.com/in/walter-andrade-neto-05961737/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href="https://wa.me/5511954823904?text=Ol%C3%A1%2C%20vim%20pelo%20site...%20oq%20tem%20de%20novo%3F" target="_blank" rel="noopener noreferrer">WhatsApp</a>
        </nav>
      </div>
    `;

    this.form = this.root.querySelector('#intent-form');
    this.input = this.root.querySelector('#intent-input');
    this.counter = this.root.querySelector('#intent-counter');
    this.errorEl = this.root.querySelector('#intent-error');
    this.submitBtn = this.root.querySelector('.intent-form__submit');
    this.memoryEl = this.root.querySelector('#intent-memory');

    this.input.addEventListener('input', () => this._onInput());
    this.input.addEventListener('focus', () => {
      this.onFocusChange?.(true);
    });
    this.input.addEventListener('blur', () => {
      this.onFocusChange?.(false);
    });
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      void this._submit();
    });
    this._renderMemory();
    this.onQueryChange?.(this.input.value || '');
  }

  _renderMemory() {
    if (!this.memoryEl) return;
    if (!this._history.length && !this._suggestions.length) {
      this.memoryEl.hidden = true;
      this.memoryEl.innerHTML = '';
      return;
    }

    const historyHtml = this._history.length
      ? `
        <div class="intent-memory__block">
          <p class="intent-memory__title">Suas buscas neste dispositivo</p>
          <ul class="intent-memory__list">
            ${this._history
              .map(
                (h) => `
              <li>
                <button type="button" class="intent-memory__chip" data-replay="${escapeAttr(h.id)}">
                  ${escapeHtml(h.query)}
                </button>
              </li>`,
              )
              .join('')}
          </ul>
        </div>`
      : '';

    const suggestHtml = this._suggestions.length
      ? `
        <div class="intent-memory__block">
          <p class="intent-memory__title">Sugestões (sem Gemini)</p>
          <ul class="intent-memory__list">
            ${this._suggestions
              .map(
                (s) => `
              <li>
                <button type="button" class="intent-memory__chip intent-memory__chip--suggest" data-suggest="${escapeAttr(s.query)}" title="${escapeAttr(s.reason || '')}">
                  ${escapeHtml(s.label || s.query)}
                </button>
              </li>`,
              )
              .join('')}
          </ul>
        </div>`
      : '';

    this.memoryEl.hidden = false;
    this.memoryEl.innerHTML = historyHtml + suggestHtml;

    this.memoryEl.querySelectorAll('[data-replay]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this._busy || !this.onReplay) return;
        void this.onReplay(btn.getAttribute('data-replay'));
      });
    });
    this.memoryEl.querySelectorAll('[data-suggest]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this._busy) return;
        const q = btn.getAttribute('data-suggest') || '';
        if (this.onSuggest) void this.onSuggest(q);
        else {
          this.input.value = q;
          this._onInput();
          void this._submit();
        }
      });
    });
  }

  _onInput() {
    const value = this.input.value;
    const len = value.length;
    this.counter.textContent = `${len} / ${QUERY_MAX_LEN}`;
    this.counter.classList.toggle('is-near-limit', len >= QUERY_MAX_LEN - 10);
    this.counter.classList.toggle('is-at-limit', len >= QUERY_MAX_LEN);
    this.form.classList.add('is-typing');
    window.clearTimeout(this._typeTimer);
    this._typeTimer = window.setTimeout(() => {
      this.form.classList.remove('is-typing');
    }, 180);
    this._setError('');
    this.onQueryChange?.(value);
  }

  _setError(msg) {
    if (!msg) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      return;
    }
    this.errorEl.hidden = false;
    this.errorEl.textContent = msg;
  }

  async _submit() {
    if (this._busy) return;
    const cleaned = sanitizeQuery(this.input.value);
    if (!cleaned.ok) {
      this._setError(cleaned.error);
      this.input.focus();
      this.form.classList.add('is-shake');
      window.setTimeout(() => this.form.classList.remove('is-shake'), 420);
      return;
    }
    this.input.value = cleaned.text;
    this._onInput();
    await this.onSubmit(cleaned.text);
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/`/g, '&#96;');
}
